import { create } from "zustand";

type LangText = {
  id?: string;
  en?: string;
};

type CompanyProfile = {
  name: string;
  about_us: LangText;
  website: string;
  logo: string;
  gallery_images: string[];
  address: string;
  maps: string;
  culture: LangText;
  facebook: string;
  instagram: string;
  tiktok: string;
  linkedin: string;
  youtube: string;
  industry_id: string;
  phone: string;
  benefits: string[];
  company_size_id: string;
  npwp: number;
  package_status: string;
  npwp_file: string;
  nib: string;
  active_day: string;
  package_id: string;
  slot_posting: string;
  package_name: string;
  start_date: string;
};

type HrProfile = {
  name: string;
  photo: string;
  email: string;
  job_title: string;
  phone: string;
};

type SocialMediaPayload = {
  instagram?: string;
  facebook?: string;
  tiktok?: string;
  linkedin?: string;
};

type AddressPayload = {
  address: string;
  maps: string;
};

type companyVerification = {
  npwp: string;
  npwp_file: File;
  nib?: string;
  package_id: string;
};

type UpdateGalleryResponse = {
  success: boolean;
  message?: string;
  data?: {
    gallery_images: string[];
  };
};

type CompanyProfileStore = {
  profile: CompanyProfile | null;
  packages: any[];
  hrProfile: HrProfile | null;

  fetchCompanyProfile: () => Promise<void>;
  fetchCompanyPackage: () => Promise<void>;
  fetchCompanyProfileByLang: (lang: string) => Promise<CompanyProfile | null>;
  fetchHrProfile: () => Promise<void>;

  updateAboutUs: (
    payload: { about_us: { id: string; en: string } }
  ) => Promise<{ success: boolean; message?: string }>;

  updateCulture: (
    payload: { culture: { id: string; en: string } }
  ) => Promise<{ success: boolean; message?: string }>;

  updateBenefits: (
    benefitIds: number[]
  ) => Promise<{ success: boolean; message?: string }>;

  updateSocialMedia: (
    payload: SocialMediaPayload
  ) => Promise<{ success: boolean; message?: string }>;

  updateAddress: (
    payload: AddressPayload
  ) => Promise<{ success: boolean; message?: string }>;

  // 🔹 Sesuaikan return type agar ada data
  updateGalleryImages: (files: File[]) => Promise<UpdateGalleryResponse>;

  updateCompanyDetails: (
    payload: {
      name: string;
      industry_id: string;
      company_size_id: string;
      website: string;
      phone: string;
      logo?: File | null;
    }
  ) => Promise<{ success: boolean; message?: string }>;

  updateCompanyVerification: (
    payload: companyVerification
  ) => Promise<{ success: boolean; message?: string }>;
};


const getLangFromUrl = () => {
  if (typeof window === "undefined") return "en";
  const segment = window.location.pathname.split("/")[1];
  return segment === "id" ? "id" : "en";
};

const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed?.state?.accessToken || null;
  } catch (e) {
    console.error("❌ Failed to parse auth token:", e);
    return null;
  }
};

const updateIsVerifiedInStorage = () => {
  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return;

    const parsed = JSON.parse(raw);

    if (parsed?.state?.isVerified === true) return; // sudah true → skip

    parsed.state.isVerified = true;

    localStorage.setItem("auth-storage", JSON.stringify(parsed));
  } catch (e) {
    console.error("Failed to update auth-storage", e);
  }
};

export const useCompanyProfileStore = create<CompanyProfileStore>((set) => ({
  profile: null,
  hrProfile: null,
  packages: [],
  

  fetchCompanyProfile: async () => {
    try {
      const token = getAccessToken();
      if (!token) {
        console.warn("No access token");
        return;
      }

      const res = await fetch(
        "https://admin.talentlinkindo.com/api/company/company-profile",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        throw new Error("Failed to fetch company profile");
      }

      const json = await res.json();
      const profile = json.data?.[0] || null;

      // ✅ set ke zustand dulu
      set({ profile });

      // ✅ SYNC KE LOCAL STORAGE KALAU APPROVED
      try {
        const raw = localStorage.getItem("auth-storage");
        if (!raw) return;

        const parsed = JSON.parse(raw);

        if (profile?.package_status === "approved") {
          // kalau sudah approved, set isVerified true
          parsed.state.isVerified = true;
        } else if (profile?.package_status === "rejected" || profile?.package_status === "reviewed") {
          // kalau rejected atau reviewed, set isVerified false
          parsed.state.isVerified = false;
        }

        localStorage.setItem("auth-storage", JSON.stringify(parsed));
      } catch (e) {
        console.error("Failed to update auth-storage", e);
      }


    } catch (error) {
      console.error("❌ Failed to fetch company profile:", error);
    }
  },


  fetchHrProfile: async () => {
    try {
      const token = getAccessToken();
      if (!token) {
        console.warn("No access token");
        return;
      }

      const res = await fetch(
        `https://admin.talentlinkindo.com/api/company/user-profile`,
        {
          method: "GET",
          headers: {
            Accept: `application/json`,
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 401) {
        // Unauthorized, hapus auth storage dan redirect
        localStorage.removeItem("auth-storage");
        window.location.href = "/";
        return;
      }

      if (!res.ok) {
        throw new Error("Failed to fetch company profile");
      }

      const json = await res.json();
      set({ hrProfile: json.data || null });
    } catch (error) {
      console.error("❌ Failed to fetch company profile:", error);
    }
  },



  fetchCompanyPackage: async () => {
    try {
      const lang = getLangFromUrl();
      const res = await fetch(
        `https://admin.talentlinkindo.com/api/${lang}/package`
      );

      if (!res.ok) throw new Error("Failed to fetch package");

      const json = await res.json();
      set({ packages: json.data || [] });
    } catch (error) {
      console.error("❌ Failed to fetch company package:", error);
    }
  },

  fetchCompanyProfileByLang: async (lang) => {
    try {
      const res = await fetch(
        `https://admin.talentlinkindo.com/api/${lang}/company-profile`
      );

      if (!res.ok) throw new Error("Failed to fetch company profile by lang");

      const json = await res.json();
      return json.data?.[0] || null;
    } catch (err) {
      console.error("❌ Failed to fetch lang profile:", err);
      return null;
    }
  },

  updateAboutUs: async (payload) => {
    const token = getAccessToken();
    if (!token) {
      return { success: false, message: "No access token" };
    }

    try {
      const res = await fetch(
        "https://admin.talentlinkindo.com/api/company/profile/about",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result?.message || "Failed to update About Us");
      }

      set({
        profile: result.data || null,
      });

      return {
        success: true,
        message: result.message, // ⬅️ "Company about section updated successfully."
      };
    } catch (e: any) {
      console.error("❌ Update About Us failed:", e);
      return {
        success: false,
        message: e.message || "Unknown error",
      };
    }
  },

  updateCulture: async (payload) => {
    const token = getAccessToken();
    if (!token) {
      return { success: false, message: "No access token" };
    }

    try {
      const res = await fetch(
        "https://admin.talentlinkindo.com/api/company/profile/culture",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result?.message || "Failed to update About Us");
      }

      set({
        profile: result.data || null,
      });

      return {
        success: true,
        message: result.message, // ⬅️ "Company about section updated successfully."
      };
    } catch (e: any) {
      console.error("❌ Update About Us failed:", e);
      return {
        success: false,
        message: e.message || "Unknown error",
      };
    }
  },

  updateBenefits: async (benefitIds) => {
    const token = getAccessToken();
    if (!token) {
      return { success: false, message: "No access token" };
    }

    try {
      const res = await fetch(
        "https://admin.talentlinkindo.com/api/company/profile/benefits",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            benefit_ids: benefitIds,
          }),
        }
      );

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result?.message || "Failed to update benefits");
      }

      // 🔄 update profile di store kalau API balikin data terbaru
      set({
        profile: result.data || null,
      });

      return {
        success: true,
        message: result.message, // contoh: "Company benefits updated successfully."
      };
    } catch (e: any) {
      console.error("❌ Update Benefits failed:", e);
      return {
        success: false,
        message: e.message || "Unknown error",
      };
    }
  },

  updateSocialMedia: async (payload) => {
    const token = getAccessToken();
    if (!token) {
      return { success: false, message: "No access token" };
    }

    try {
      const res = await fetch(
        "https://admin.talentlinkindo.com/api/company/profile/social-media",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result?.message || "Failed to update social media");
      }

      // 🔄 update profile di store
      set({
        profile: result.data || null,
      });

      return {
        success: true,
        message: result.message, // contoh: "Company social media updated successfully."
      };
    } catch (e: any) {
      console.error("❌ Update Social Media failed:", e);
      return {
        success: false,
        message: e.message || "Unknown error",
      };
    }
  },

  updateAddress: async (payload) => {
    const token = getAccessToken();
    if (!token) {
      return { success: false, message: "No access token" };
    }

    try {
      const formData = new FormData();
      formData.append("address", payload.address);
      formData.append("maps", payload.maps);

      const res = await fetch(
        "https://admin.talentlinkindo.com/api/company/profile/address-maps",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result?.message || "Failed to update address");
      }

      // 🔄 update profile di store
      set({
        profile: result.data || null,
      });

      return {
        success: true,
        message: result.message || "Office address updated successfully",
      };
    } catch (e: any) {
      console.error("❌ Update Address failed:", e);
      return {
        success: false,
        message: e.message || "Unknown error",
      };
    }
  },

  updateGalleryImages: async (files: File[]): Promise<UpdateGalleryResponse> => {
  const token = getAccessToken();
  if (!token) return { success: false, message: "No access token" };

  try {
    const formData = new FormData();
    files.forEach((file) => formData.append("gallery_images[]", file));

    const res = await fetch(
      "https://admin.talentlinkindo.com/api/company/profile/gallery-images",
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      }
    );

    const result = await res.json();

    if (!res.ok) throw new Error(result?.message || "Failed to upload gallery images");

    set({ profile: result.data || null });

    return { success: true, message: result.message, data: result.data };
  } catch (e: any) {
    console.error("❌ Upload Gallery failed:", e);
    return { success: false, message: e.message };
  }
},


  updateCompanyDetails: async (payload) => {
    const token = getAccessToken();
    if (!token) {
      return { success: false, message: "No access token" };
    }

    try {
      const formData = new FormData();

      formData.append("name", payload.name);
      formData.append("industry_id", payload.industry_id);
      formData.append("company_size_id", payload.company_size_id);
      formData.append("website", payload.website);
      formData.append("phone", payload.phone);

      if (payload.logo) {
        formData.append("logo", payload.logo);
      }

      const res = await fetch(
        "https://admin.talentlinkindo.com/api/company/profile/details",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          body: formData,
        }
      );

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result?.message || "Failed to update company details");
      }

      // 🔄 update profile di store
      set({
        profile: result.data || null,
      });

      return {
        success: true,
        message: result.message || "Company profile updated successfully",
      };
    } catch (e: any) {
      console.error("❌ Update Company Details failed:", e);
      return {
        success: false,
        message: e.message || "Unknown error",
      };
    }
  },

  updateCompanyVerification: async (payload) => {
    const token = getAccessToken();
    if (!token) {
      return { success: false, message: "No access token" };
    }

    try {
      const formData = new FormData();
      formData.append("npwp", payload.npwp);
      formData.append("npwp_file", payload.npwp_file);
      formData.append("nib", payload.nib ?? "");
      formData.append("package_id", payload.package_id);

      const res = await fetch(
        "https://admin.talentlinkindo.com/api/company/profile/verification",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result?.message || "Failed to update address");
      }

      // 🔄 update profile di store
      set({
        profile: result.data || null,
      });

      return {
        success: true,
        message: result.message || "Office address updated successfully",
      };
    } catch (e: any) {
      console.error("❌ Update Address failed:", e);
      return {
        success: false,
        message: e.message || "Unknown error",
      };
    }
  },

}));
